import { useOptionalCoach } from '@/features/coach/CoachContext'
import { useAppStore } from '@runtime/core/store/appStore'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

const DESKTOP_QUERY = '(min-width: 1024px)'
export function usePlayHeaderActions() {
  const { pathname } = useLocation()
  const coachOpen = useOptionalCoach()?.isOpen ?? false
  const accountOpen = useAppStore(state => state.ui.loginOpen)
  const [mobile, setMobile] = useState(() => !window.matchMedia(DESKTOP_QUERY).matches)
  const [open, setOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const returnAction = useRef<HTMLElement | null>(null)
  const previous = useRef({ accountOpen, coachOpen })
  const close = useCallback(() => {
    // React may already have removed popover after a queued media-query event.
    const menu = menuRef.current
    if (menu?.hasAttribute('popover')) menu.hidePopover()
  }, [])
  const restoreFocus = useCallback(() => {
    const target = window.matchMedia(DESKTOP_QUERY).matches
      ? returnAction.current
      : buttonRef.current
    if (target?.isConnected) target.focus({ preventScroll: true })
  }, [])
  const closeBeforeAction = (action?: HTMLElement) => {
    returnAction.current = action || null
    close()
    if (mobile) buttonRef.current?.focus({ preventScroll: true })
  }
  useEffect(() => {
    const media = window.matchMedia(DESKTOP_QUERY)
    const update = () => {
      if (media.matches) {
        close()
        if (document.activeElement === buttonRef.current)
          buttonRef.current?.closest('header')?.querySelector('a')?.focus({ preventScroll: true })
      }
      setMobile(!media.matches)
    }
    media.addEventListener('change', update)
    return () => media.removeEventListener('change', update)
  }, [close])
  useEffect(() => close(), [pathname, close])
  useEffect(() => {
    if (coachOpen || accountOpen) close()
    if (
      (previous.current.accountOpen && !accountOpen) ||
      (previous.current.coachOpen && !coachOpen)
    )
      restoreFocus()
    previous.current = { accountOpen, coachOpen }
  }, [accountOpen, coachOpen, close, restoreFocus])
  return { mobile, open, setOpen, menuRef, buttonRef, closeBeforeAction, restoreFocus }
}
